import { AnimatePresence, motion } from 'framer-motion';
import { FeaturedArena } from 'modules/jogador/home/content';
import { EASE_OUT } from 'shared/components/motion/tokens';
import { Reveal } from 'shared/components/motion/Reveal';
import { ArenaCard } from 'modules/jogador/home/components/ArenaCard';
import { Badge } from 'shared/components/ui/Badge';
import { AsyncSection } from 'shared/components/ui/LoadingState';
import { ArenaCardSkeleton } from 'modules/jogador/home/components/ArenaCardSkeleton';

interface FeaturedArenasSectionProps {
  city: string;
  arenas: FeaturedArena[];
  isLoading: boolean;
  error: string | null;
}

export function FeaturedArenasSection({ city, arenas, isLoading, error }: FeaturedArenasSectionProps) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-8">
      <Reveal className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold text-ink">Arenas em Destaque em {city}</h2>
        <Badge tone="accent">{arenas.length} arenas próximas</Badge>
      </Reveal>

      <AsyncSection
        isLoading={isLoading}
        error={error}
        loadingLabel="Buscando arenas..."
        skeleton={
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
            <ArenaCardSkeleton />
          </div>
        }
      >
        {arenas.length === 0 ? (
          <p className="mt-6 text-sm text-ink-secondary">
            Nenhuma arena encontrada para esse filtro. Tente outro esporte ou bairro.
          </p>
        ) : (
          // layout + popLayout: ao filtrar por esporte/bairro os cards saem e os restantes deslizam para o lugar.
          <motion.div layout className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {arenas.map((arena, index) => (
                <motion.div
                  key={arena.id}
                  className="h-full"
                  layout
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, ease: EASE_OUT, delay: index * 0.08 }}
                >
                  <ArenaCard arena={arena} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </AsyncSection>
    </section>
  );
}
